import React, { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { LayoutConfig } from '../../constants/layouts';

/**
 * D19 — the confirm a caregiver sees before committing to a layout that has no
 * 911 button (cb_1, vi_1, vi_2).
 *
 * ⚠️ The copy is CEO-locked (2026-08-25) and lives in `layoutWarning.*`. It
 * names a category of product AidFone does not sell — a wristband or pendant
 * panic button from a specialised provider — because for a senior living alone
 * that is the honest answer, and the wizard used to give none.
 *
 * ⚠️ Note the deliberate EN/FR term split, which is not a translation slip:
 * English says "layout", French says « modèle ». D20 makes that split the rule
 * across every customer-facing surface.
 *
 * The primary action is "choose another layout" — i.e. the SAFE one. The
 * dismissive path is secondary and spells out what is being accepted, by name.
 * Do not swap the visual weight of the two buttons.
 */
export interface LayoutNo911DialogProps {
  /** The no-911 layout the caregiver just picked. */
  layout: LayoutConfig;
  /** "Choose another layout" — cancel, leave the selection as it was. */
  onChooseAnother: () => void;
  /** "I understand, continue" — proceed with this layout. */
  onContinue: () => void;
}

export const LayoutNo911Dialog: FC<LayoutNo911DialogProps> = ({
  layout,
  onChooseAnother,
  onContinue,
}) => {
  const { t } = useTranslation();
  const safeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    // Focus the safe choice, not the dismissive one: Enter on an unread dialog
    // should back out, never accept a phone with no 911 button.
    safeButtonRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onChooseAnother();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onChooseAnother]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
      role="alertdialog"
      aria-modal="true"
      // The locked copy has no heading, so the body IS the accessible name.
      // Deliberately not inventing a title to satisfy aria-labelledby.
      aria-describedby="layout-no-911-body"
    >
      {/* No click-outside dismissal on purpose. Every way out of this dialog is
          an explicit choice between the two buttons — a stray backdrop click
          must not silently resolve a safety question. */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="px-6 py-5 flex gap-3">
          <span
            className="material-symbols-outlined text-3xl text-brand-accent shrink-0"
            aria-hidden="true"
          >
            report
          </span>
          <p
            id="layout-no-911-body"
            className="text-sm text-brand-text dark:text-slate-200 leading-relaxed"
          >
            {t('layoutWarning.body', { layout: layout.name })}
          </p>
        </div>

        <div className="px-6 pb-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button
            type="button"
            onClick={onContinue}
            className="px-4 py-2.5 text-sm font-semibold text-brand-text-light dark:text-slate-400 hover:text-brand-text dark:hover:text-white hover:bg-brand-bg-warm dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            {t('layoutWarning.continueAnyway', { layout: layout.name })}
          </button>
          <button
            ref={safeButtonRef}
            type="button"
            onClick={onChooseAnother}
            className="px-5 py-2.5 text-sm font-bold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg transition-colors"
          >
            {t('layoutWarning.chooseAnother')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LayoutNo911Dialog;
